import { Component, useEffect, useRef, useState } from "react";
import { useCallback } from "react";
import {
  Edge,
  Handle,
  NodeProps,
  Position,
  ReactFlowInstance,
  useReactFlow,
  useStore,
  XYPosition,
} from "reactflow";
import { getChatGPTBrainstormStream } from "../../../api/openai-api";
import "./brainstorm-node.scss";
import { ReactComponent as DragHandle } from "../../../assets/drag-handle.svg";

import BrainstormInput from "./brainstorm-input/brainstorm-input";
import { ResponseState } from "../../components/input.model";
import { createBrainstormNode } from "./brainstorm-node.helper";
import { ZoomState } from "../node.model";
import { createChatNode } from "../response-node/response-node.helper";

import { ChatNodeData, TypeChatNode } from "../response-node/response-node.model";
import QuestionNode from "./question-node";
import { QuestionNodeData, TypeQuestionNode } from "./question-node.model";
import { devFlags } from "../../utils";

import { DownloadOutlined } from '@ant-design/icons';
import { Button, Radio, Space, Divider } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';

import { ChatCompletionRequestMessage } from "openai";
import { getStoredApiKey } from "../../components/ApiKeyModal";

const https = require('https-browserify'); // using it to stream text from openAI

type BrainstormState = {
  input: string;
  response: string; // Singular response. more follow ups/responses belong in another chatnode
  responseIsLoading: boolean;
  reactFlowInstance: ReactFlowInstance;
};

const zoomSelector = (s: any) => s.transform[2];

function isFiveWs(arr:any, val:any) {
  return arr.some((arrVal:any) => val === arrVal || val.includes(arrVal) || arrVal.includes(val));
}

interface questionSet {
  Why: string[],
  What: string[],
  When: string[],
  Where: string[],
  How: string[]
}

const BrainstormNode = (props: NodeProps) => {
  const [input, setInput] = useState(props.data.state.input ?? '');
  const [questions, setQuestions] = useState(props.data.state.questions ?? {});
  const [responseInputState, setResponseInputState] = useState<ResponseState>(
    props.data.state.responseInputState ?? ResponseState.INPUT);
  const [filterValue, setFilterValue] = useState('[Why, What, When, Where, How]');
  const zoom: number = useStore(zoomSelector);

  const showContent = zoom >= 0.35;

  const reactFlowInstance = useReactFlow();

  const generateQuestions = async (keyword: string) => {
    if (!keyword) return;

    if (devFlags.disableOpenAI) {
      setResponseInputState(ResponseState.COMPLETE);
      return;
    }

    setResponseInputState(ResponseState.LOADING);

    // const chatHistory: ChatCompletionRequestMessage[] = [];

    // // Stream Brainstorm text
    // return getChatGPTBrainstormStream([], keyword, (text: string) => {
    //   // skip new lines

    //   // If don't push to array and 

    // })

    let text = '';

    const fiveWs = ['Why ', 'What ','How ', 'When ', 'Where'];
    let fiveWsType = '';
    let questionNum: number;
    let pushToArray = false;
    let question_set:questionSet = {
      'Why': [],
      'When': [],
      'Where': [],
      'How': [],
      'What': [],
    };

    // reference: https://github.com/openai/openai-node/issues/18
    const req = https.request({
      hostname:"api.openai.com",
      port:443,
      path:"/v1/chat/completions",
      method:"POST",
      headers:{
        "Content-Type":"application/json",
        "Authorization":"Bearer "+ getStoredApiKey(),
      }
    }, function(res:any){
      let stream_text = '';
      res.on('data', (chunk: string) => {
        const lines = chunk.toString().split('\n').filter(line => line.trim() !== '');
        for (const line of lines) {
            const message = line.replace(/^data: /, ''); // remove unnecessary label
            // console.log('message', message);
            if (message === '[DONE]') { // this means there is no more data to stream
                return; // Stream finished
            }
            try {
                const parsed = JSON.parse(message);
                const content: string | undefined = parsed.choices?.[0]?.delta?.content;

                // skip chunks with no content (e.g. final stop chunk with empty delta)
                if (content === undefined || content === null) continue;

                if (!pushToArray && parseInt(content)) {
                  pushToArray = true;
                  questionNum = parseInt(content);
                }
                // if pushToArray is false and word is one of five Ws
                if (isFiveWs(fiveWs, content)) { // if it's one of four W's and 1H's
                  pushToArray = true;
                  fiveWsType = content;
                }

                if (pushToArray && !content.includes('?')) { // if it's something other than '?', add
                  stream_text = stream_text + content;
                }
                else if (pushToArray && content.includes('?')) { // if it's '?', end
                  stream_text = stream_text + '?';
                  fiveWsType = fiveWsType.replaceAll(' ', '');
                  question_set[fiveWsType as keyof questionSet].push(stream_text);
                  setQuestions({...question_set}); // save to response and update text in node
                  // initialize
                  fiveWsType = '';
                  stream_text = '';
                  pushToArray = false;
                }
            } catch(error) {
                // skip malformed lines
            }
        }
        
        
        // text = text + stream_text; // attach to previous text
        
      });
      res.on('end', () => { // once stream is done,
        setResponseInputState(ResponseState.COMPLETE); // update icon to display that stream is over
      });
    })

    const prompt =
      "I need to learn about " +
      keyword +
      `. Give me a total of 25 questions, with questions starting with "why", 5 questions starting with "what", 5 questions starting with "when", 5 questions starting with "where", and 5 questions starting with "how". Do not add number in front of the questions.`;
    const gptPrompt: string = `${prompt}\n\n`;
    
    const body = JSON.stringify({
      model:"gpt-3.5-turbo",
      messages: [{ role: "user", content: gptPrompt }],
      temperature: 0.7,
      top_p: 1.0,
      frequency_penalty: 0.5,
      presence_penalty: 0.7,
      stream:true
    })
    
    req.on('error', (e:any) => {
      console.error("problem with request:" + e.message);
    });
    
    req.write(body);
    
    req.end();

    // const response =
    //   (await getGPT3Questions(keyword)) || "Error: no response received";

    // await setResponseInputState(ResponseState.COMPLETE);
    // setQuestions(response[1]);
  };

  useEffect(() => {
    reactFlowInstance.setNodes((nodes) => nodes.map(node => {
      if (node.id === props.id) {
        node.data.state = {
          input,
          questions,
          responseInputState
        };
      }
      return node;
    }));
  }, [reactFlowInstance, input, questions, responseInputState]);

  useEffect(() => {
    if (props.data.state.input && props.data.state.responseInputState === ResponseState.LOADING) {
      generateQuestions(input);
    } else if (props.data.state.responseInputState === ResponseState.INPUT) {    

    }
  }, [])

  const Placeholder = () => (
    <div className="placeholder">
      <div />
      <div />
      <div />
      <br></br>
      <div />
      <div />
      <div />
      <br></br>
      <div />
      <div />
      <div />
      <br></br>
      <div />
      <div />
      <div />
      <br></br>
      <div />
      <div />
      <div />
    </div>
  );

  return (
    <div className={`node brainstorm-node`}>
      <DragHandle className="drag-handle" />
      {/* <Handle type="source" position={Position.Right} /> */}
      <Handle id="target-e-1" type="target" position={Position.Top} className="node-handle-direct" />
      <Handle id="source-e-1" type="source" position={Position.Bottom} className="node-handle-direct" />
      <BrainstormInput
        placeholder={props.data.placeholder}
        generateQuestions={generateQuestions}
        responseState={responseInputState}
        id={props.id}
        input={input}
        setInput={setInput}
      />
      { props.data.state.responseInputState === ResponseState.COMPLETE ? (
        <div className="semantic-zoom-control">
          <Radio.Group value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
            <Radio.Button value="Why">Why</Radio.Button>
            <Radio.Button value="What">What</Radio.Button>
            <Radio.Button value="When">When</Radio.Button>
            <Radio.Button value="Where">Where</Radio.Button>
            <Radio.Button value="How">How</Radio.Button>
            <Radio.Button value="[Why, What, When, Where, How]">All</Radio.Button>
          </Radio.Group>
        </div>
      ): <></>}
      {questions ? (
        <>
          {showContent ? (
            // <div className="brainstorm-response">{response}</div>
            <>
              {Object.entries(questions).filter((questionType: any) => filterValue.includes(questionType[0])).map((elem: any, questionTypeIndex: number) => (
                <div className="brainstorm-response-block" key={questionTypeIndex}>
                  {
                    elem[1].map((question: any, index:number) => <div key={index}>
                      <QuestionNode
                        keyword={input}
                        index={(questionTypeIndex * 5) + index}
                        fiveWsType={elem[0]}
                        question={question}
                        parentId={props.id}
                      />
                    </div>)
                  }
                </div>
              ))
              }
              {/* { questions } */}
            </>
          ) : (
            <Placeholder /> // show grey rectangle when zoomed out 
          )}
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default BrainstormNode;
